import { computed } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  DEFAULT_SHORTCUTS,
  SHORTCUT_ACTIONS,
  keysToDisplayTokens,
} from 'dashboard/components/widgets/modal/constants';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

/**
 * 可自定义快捷键：读取 / 写入用户 ui_settings.keyboard_shortcuts 覆盖表。
 * 覆盖为空时回退到 DEFAULT_SHORTCUTS。
 */
export function useKeyboardShortcuts() {
  const { uiSettings, updateUISettings } = useUISettings();

  /** 用户自定义覆盖：`{ [actionId]: 'Alt+KeyX' }` */
  const overrides = computed(
    () => uiSettings.value?.keyboard_shortcuts || {}
  );

  /**
   * 获取某动作当前生效的 tinykeys 绑定字符串。
   * @param {string} action
   * @returns {string}
   */
  const getKeySet = action => {
    const override = overrides.value[action];
    if (override) return override;
    return DEFAULT_SHORTCUTS[action]?.keys || '';
  };

  /** 全部动作 → 当前生效绑定（依赖 overrides，便于响应式刷新） */
  const resolvedShortcuts = computed(() => {
    const result = {};
    Object.keys(DEFAULT_SHORTCUTS).forEach(action => {
      result[action] = getKeySet(action);
    });
    return result;
  });

  /**
   * 查找已被其他动作占用的组合。
   * @param {string} keys
   * @param {string} excludeAction 当前正在编辑的动作，排除自身
   * @returns {string|null} 冲突的 action id
   */
  const findConflict = (keys, excludeAction) => {
    if (!keys) return null;
    return (
      Object.keys(DEFAULT_SHORTCUTS).find(
        action => action !== excludeAction && getKeySet(action) === keys
      ) || null
    );
  };

  /**
   * 为动作写入新快捷键；与默认相同则删除覆盖项。
   * @param {string} action
   * @param {string} keys
   * @returns {{ ok: boolean, reason?: string, conflict?: string }}
   */
  const setKeySet = (action, keys) => {
    if (!DEFAULT_SHORTCUTS[action]) return { ok: false, reason: 'unknown' };

    const conflict = findConflict(keys, action);
    if (conflict) {
      return { ok: false, reason: 'conflict', conflict };
    }

    const next = { ...overrides.value };
    if (keys === DEFAULT_SHORTCUTS[action].keys) {
      delete next[action];
    } else {
      next[action] = keys;
    }

    updateUISettings({ keyboard_shortcuts: next });
    return { ok: true };
  };

  /** 清空全部覆盖，恢复默认快捷键 */
  const restoreDefaults = () => {
    updateUISettings({ keyboard_shortcuts: {} });
  };

  /**
   * 获取动作对应的展示芯片文案。
   * @param {string} action
   * @returns {string[]}
   */
  const getDisplayTokens = action => keysToDisplayTokens(getKeySet(action));

  return {
    SHORTCUT_ACTIONS,
    overrides,
    resolvedShortcuts,
    getKeySet,
    setKeySet,
    restoreDefaults,
    findConflict,
    getDisplayTokens,
  };
}

/**
 * 按 action id 注册快捷键处理器：内部用当前 getKeySet 组装 tinykeys 映射，
 * 并在覆盖变更后自动重新绑定。
 * @param {Object.<string, Function|{action: Function, allowOnFocusedInput?: boolean}>} actionHandlers
 *   key 为 SHORTCUT_ACTIONS.*，value 为回调或 `{ action, allowOnFocusedInput }`
 */
export function useShortcutEvents(actionHandlers = {}) {
  const { getKeySet, resolvedShortcuts } = useKeyboardShortcuts();

  /** 当前生效的 tinykeys 事件表；依赖 resolvedShortcuts 以便编辑后刷新 */
  const keyboardEvents = computed(() => {
    const current = resolvedShortcuts.value;
    const events = {};

    Object.entries(actionHandlers).forEach(([action, handler]) => {
      const keys = current[action] || getKeySet(action);
      if (!keys || !handler) return;
      events[keys] = handler;
    });

    return events;
  });

  return useKeyboardEvents(keyboardEvents);
}
